// src/pages/ForgotPassword.js
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/NotificationContainer';
import { analyticsEvents } from '../utils/analyticsEvents';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { notifications, showSuccess, showError, removeNotification } = useNotification();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
      analyticsEvents.forgotPasswordRequested();
      showSuccess(t('auth.forgotPasswordSent', 'If an account exists with this email, you will receive a password reset link shortly.'));
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Request failed';
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 px-4 transition-colors">
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
      <div className="max-w-md w-full bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg p-8 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <h2 className="text-2xl font-bold text-center text-shayek-light-900 dark:text-shayek-dark-900 mb-2">
          {t('auth.forgotPassword', 'Forgot Password')}
        </h2>
        <p className="text-center text-shayek-light-600 dark:text-shayek-dark-600 mb-6 text-sm">
          {t('auth.forgotPasswordHint', 'Enter your email and we’ll send you a link to reset your password.')}
        </p>

        {sent ? (
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-800 dark:text-green-300 text-sm text-center">
            {t('auth.forgotPasswordSent', 'If an account exists with this email, you will receive a password reset link shortly.')} Check your inbox and spam folder.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                {t('auth.email', 'Email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 bg-shayek-primary dark:bg-shayek-red-light text-white font-medium rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-dark disabled:opacity-50 transition-colors"
            >
              {loading ? t('common.loading', 'Sending...') : t('auth.sendResetLink', 'Send reset link')}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-shayek-light-600 dark:text-shayek-dark-600">
          <Link to="/login" className="text-shayek-primary hover:text-shayek-primary-600 dark:text-shayek-red-light dark:hover:text-shayek-red-dark font-medium transition-colors">
            {t('auth.backToLogin', 'Back to sign in')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
